import { useState, useEffect, useRef, useCallback, memo } from 'react';
import { 
  Card, 
  Button, 
  Space, 
  Typography, 
  Row, 
  Col, 
  Statistic,
  Table,
  Tag,
  Progress,
  Modal,
  Form,
  Select,
  Input,
  DatePicker,
  message,
  Tooltip
} from 'antd';
import { 
  SendOutlined,
  TeamOutlined,
  FileTextOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  BarChartOutlined,
  EyeOutlined,
  CalendarOutlined,
  ClockCircleOutlined,
  PlusOutlined,
  DeleteOutlined,
  FilterOutlined,
  ReloadOutlined,
  ThunderboltOutlined,
  LoadingOutlined,
  SearchOutlined,
  CopyOutlined,
  ToolOutlined,
  LockOutlined,
  InfoCircleOutlined
} from '@ant-design/icons';
import moment from 'moment';
import axiosInstance from '../../../../config/axiosConfig';
import { useWaba } from '../../../../context/wabaContext';
import WriteGuard from '../../../../components/WriteGuard';
import { AudienceBadge } from '../../../../components/audience';
import AdvancedBroadcastBuilder from './AdvancedBroadcastBuilder';
import SendCampaignModal from './SendCampaignModal';
import CampaignDetailsPage from './CampaignDetailsPage';
import { useNavigate } from 'react-router-dom';
import { useWebSocketClient } from '../../../../context/websocketContext';
import { Skeleton as BoneSkeleton } from 'boneyard-js/react';
import './BroadcastPage.css';

const { Title, Text } = Typography;
const { Option } = Select;

const normalizeScheduleStatus = (value) => String(value || '').toLowerCase();

const getScheduleStatusLabel = (status) => ({
  scheduled: 'Scheduled',
  queued: 'Queued',
  processing: 'Processing',
  audience_fetching: 'Audience Fetching',
  completed: 'Completed',
}[status] || status?.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase()));

const isScheduleLockedStatus = (status) => ['scheduled', 'queued', 'processing', 'audience_fetching'].includes(normalizeScheduleStatus(status));

const BroadcastPage = () => {
  // State management
  const navigate = useNavigate();
  
  // WABA Details from Context for validation
  const { hasWabaDetails, loadingWabaDetails, wabaFetchError } = useWaba();
  
  // WebSocket for real-time campaign status updates
  const { statusUpdates } = useWebSocketClient();
  
  // Skeletal Loading Components with prefers-reduced-motion support
  const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const skeletonAnimation = prefersReducedMotion ? 'none' : 'broadcast-skeleton-loading 1s infinite ease-in-out';

  // eslint-disable-next-line no-unused-vars
  const StatsSkeleton = () => (
    <Row gutter={16} style={{ marginBottom: 24 }}>
      {Array.from({ length: 5 }).map((_, index) => (
        <Col xs={24} sm={12} md={8} lg={4} xl={4} key={index}>
          <Card className="stat-card">
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: 16
            }}>
              <div style={{
                width: 48,
                height: 48,
                borderRadius: 12,
                background: 'linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%)',
                animation: skeletonAnimation
              }} />
              <div style={{ flex: 1 }}>
                <div style={{
                  height: 20,
                  background: 'linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%)',
                  borderRadius: 4,
                  marginBottom: 8,
                  width: '80%',
                  animation: skeletonAnimation
                }} />
                <div style={{
                  height: 14,
                  background: 'linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%)',
                  borderRadius: 4,
                  width: '60%',
                  animation: skeletonAnimation
                }} />
              </div>
            </div>
          </Card>
        </Col>
      ))}
    </Row>
  );

  const CampaignCardSkeleton = () => (
    <Card className="campaign-card">
      <div style={{ marginBottom: 16 }}>
        <div style={{
          height: 24,
          background: 'linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%)',
          borderRadius: 6,
          marginBottom: 12,
          width: '70%',
          animation: skeletonAnimation
        }} />
        <div style={{
          height: 16,
          background: 'linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%)',
          borderRadius: 4,
          width: '90%',
          animation: skeletonAnimation
        }} />
      </div>
      
      <div style={{
        display: 'flex',
        gap: 16,
        marginBottom: 16
      }}>
        <div style={{
          height: 60,
          background: 'linear-gradient(90deg, #e8f5e8 25%, #d4f0d4 50%, #e8f5e8 75%)',
          borderRadius: 8,
          width: '100%',
          animation: skeletonAnimation
        }} />
      </div>
      
      <div style={{
        display: 'flex',
        gap: 8,
        justifyContent: 'space-between'
      }}>
        {Array.from({ length: 3 }).map((_, index) => (
          <div key={index} style={{
            height: 32,
            background: 'linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%)',
            borderRadius: 6,
            flex: 1,
            animation: skeletonAnimation
          }} />
        ))}
      </div>
    </Card>
  );

  const CampaignTableSkeleton = () => (
    <Card className="bp-skeleton-card" styles={{ body: { padding: 0 } }}>
      {/* Header row */}
      <div style={{
        padding: '14px 20px',
        background: '#f8fafc',
        borderBottom: '1px solid #e8ecf1',
        display: 'flex',
        gap: 20
      }}>
        {Array.from({ length: 6 }).map((_, index) => (
          <div key={index} className="bp-skeleton-shimmer" style={{
            height: 14,
            flex: index === 0 ? '0 0 50px' : '1',
          }} />
        ))}
      </div>
      
      {/* Data rows */}
      {Array.from({ length: 6 }).map((_, rowIndex) => (
        <div key={rowIndex} style={{
          padding: '16px 20px',
          borderBottom: rowIndex < 5 ? '1px solid #f0f2f5' : 'none',
          display: 'flex',
          gap: 20,
          alignItems: 'center'
        }}>
          {/* Avatar */}
          <div className="bp-skeleton-shimmer" style={{
            width: 42, height: 42, borderRadius: 10, flexShrink: 0
          }} />
          {/* Name + template */}
          <div style={{ flex: 2, display: 'flex', flexDirection: 'column', gap: 6 }}>
            <div className="bp-skeleton-shimmer" style={{ height: 14, width: '70%' }} />
            <div className="bp-skeleton-shimmer" style={{ height: 10, width: '50%' }} />
          </div>
          {/* Recipients */}
          <div style={{ flex: 1 }}>
            <div className="bp-skeleton-shimmer" style={{ height: 14, width: '60%' }} />
          </div>
          {/* Progress */}
          <div style={{ flex: 1.5, display: 'flex', flexDirection: 'column', gap: 6 }}>
            <div className="bp-skeleton-shimmer" style={{ height: 8, width: '100%', borderRadius: 4 }} />
            <div style={{ display: 'flex', gap: 4 }}>
              {Array.from({ length: 3 }).map((_, i) => (
                <div key={i} className="bp-skeleton-shimmer" style={{ height: 18, width: 40, borderRadius: 10 }} />
              ))}
            </div>
          </div>
          {/* Status */}
          <div style={{ flex: 1 }}>
            <div className="bp-skeleton-shimmer" style={{ height: 24, width: 90, borderRadius: 20 }} />
          </div>
          {/* Date */}
          <div style={{ flex: 1 }}>
            <div className="bp-skeleton-shimmer" style={{ height: 14, width: '70%' }} />
          </div>
        </div>
      ))}
      
      {/* Pagination */}
      <div style={{
        padding: '14px 20px',
        borderTop: '1px solid #f0f2f5',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center'
      }}>
        <div className="bp-skeleton-shimmer" style={{ height: 16, width: 120 }} />
        <div style={{ display: 'flex', gap: 6 }}>
          {Array.from({ length: 4 }).map((_, i) => (
            <div key={i} className="bp-skeleton-shimmer" style={{ height: 30, width: 30, borderRadius: 6 }} />
          ))}
        </div>
      </div>
    </Card>
  );

  // eslint-disable-next-line no-unused-vars
  const CampaignGridSkeleton = () => (
    <Row gutter={[16, 16]}>
      {Array.from({ length: 6 }).map((_, index) => (
        <Col xs={24} sm={12} lg={8} key={index}>
          <CampaignCardSkeleton />
        </Col>
      ))}
    </Row>
  );

  // eslint-disable-next-line no-unused-vars
  const WalletSkeleton = () => (
    <Card style={{ marginBottom: 16 }}>
      <div style={{
        display: 'flex',
        alignItems: 'center',
        gap: 16
      }}>
        <div style={{
          width: 40,
          height: 40,
          borderRadius: 8,
          background: 'linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%)',
          animation: skeletonAnimation
        }} />
        <div style={{ flex: 1 }}>
          <div style={{
            height: 18,
            background: 'linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%)',
            borderRadius: 4,
            marginBottom: 8,
            width: '60%',
            animation: skeletonAnimation
          }} />
          <div style={{
            height: 14,
            background: 'linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%)',
            borderRadius: 4,
            width: '40%',
            animation: skeletonAnimation
          }} />
        </div>
      </div>
    </Card>
  );
  const [showAdvancedBuilder, setShowAdvancedBuilder] = useState(false);
  const [showCampaignDetails, setShowCampaignDetails] = useState(false);
  const [selectedCampaignId, setSelectedCampaignId] = useState(null);
  const [campaignDetailVisible, setCampaignDetailVisible] = useState(false);
  const [sendCampaignVisible, setSendCampaignVisible] = useState(false);
  const [selectedCampaign, setSelectedCampaign] = useState(null);
  const [loading, setLoading] = useState(false);
  const [dataLoading, setDataLoading] = useState(false);
  const [campaigns, setCampaigns] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [totalCampaigns, setTotalCampaigns] = useState(0);
  
  // Plan-based billing state
  const [creditsLoading, setCreditsLoading] = useState(false);
  const [planStatus, setPlanStatus] = useState(null);

  // Search and filter states
  const [searchText, setSearchText] = useState('');
  const [selectedTemplateId] = useState('');
  const [debouncedSearchText, setDebouncedSearchText] = useState('');
  const [advancedFilterVisible, setAdvancedFilterVisible] = useState(false);
  const [searchLoading, setSearchLoading] = useState(false);
  
  // Advanced filters
  const [activeFilters, setActiveFilters] = useState({
    search: "",
    campaignname: "",
    templatename: "",
    status: "",
    dateFrom: null,
    dateTo: null
  });

  // Form instances
  const [filterForm] = Form.useForm();
  
  // Debouncing for search
  const searchTimeoutRef = useRef(null);
  
  // Rate limiting for refresh button
  const [refreshCooldown, setRefreshCooldown] = useState(false);
  const lastRefreshRef = useRef(0);
  const refreshInProgressRef = useRef(false);
  const REFRESH_COOLDOWN_MS = 3000; // 3 second cooldown

  // Stats data - Replace with actual API calls
  const [broadcastStats, setBroadcastStats] = useState({
    sent: 0,
    pending: 0,
    failed: 0,
    totalCampaigns: 0,
    activeCampaigns: 0
  });

  // Sample campaigns data - Replace with actual API calls
  // eslint-disable-next-line no-unused-vars
  const [recentBroadcasts, setRecentBroadcasts] = useState([]);

  // Debounce search text
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchText(searchText);
    }, 500);

    return () => clearTimeout(timer);
  }, [searchText]);

  // Debounced search function
  const debouncedSearch = useCallback(
    async (searchValue) => {
      setSearchLoading(true);
      const newFilters = { ...activeFilters, search: searchValue, campaignname: searchValue };
      setActiveFilters(newFilters);
      await fetchCampaigns(newFilters, 1, pageSize);
      setCurrentPage(1);
      setSearchLoading(false);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [activeFilters, pageSize]
  );

  // Handle search
  const handleSearch = (value) => {
    setSearchText(value);
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }
    searchTimeoutRef.current = setTimeout(() => {
      debouncedSearch(value);
    }, 500);
  };

  // Cleanup timeout on component unmount
  useEffect(() => {
    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
  }, []);

  // Handle refresh
  const handleRefresh = useCallback(async () => {
    const now = Date.now();
    
    // Prevent duplicate calls if already in progress
    if (refreshInProgressRef.current) {
      return;
    }
    
    // Check cooldown period (3 seconds)
    if (now - lastRefreshRef.current < REFRESH_COOLDOWN_MS) {
      message.warning('Please wait a moment before refreshing again');
      return;
    }
    
    // Mark refresh as in progress
    refreshInProgressRef.current = true;
    lastRefreshRef.current = now;
    setRefreshCooldown(true);
    
    // Clear existing data to trigger skeletal loading
    setCampaigns([]);
    setRecentBroadcasts([]);
    setTotalCampaigns(0);
    
    // Reset stats
    setBroadcastStats({
      sent: 0,
      pending: 0,
      failed: 0,
      totalCampaigns: 0,
      activeCampaigns: 0
    });
    
    // Reset pagination to first page
    setCurrentPage(1);
    
    // Set loading state
    setDataLoading(true);
    
    try {
      // Fetch fresh data
      await fetchCampaigns(activeFilters, 1, pageSize);
    } finally {
      refreshInProgressRef.current = false;
      
      // Start cooldown timer
      setTimeout(() => {
        setRefreshCooldown(false);
      }, REFRESH_COOLDOWN_MS);
    }
  }, [activeFilters, pageSize]);

  // Load campaigns from API
  useEffect(() => {
    const filters = { ...activeFilters, search: debouncedSearchText, campaignname: debouncedSearchText };
    fetchCampaigns(filters, currentPage, pageSize);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage, pageSize, debouncedSearchText, selectedTemplateId]);

  // Initial load on component mount
  useEffect(() => {
    fetchCampaigns();
    fetchWalletBalance();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // WebSocket real-time campaign status updates
  // Listens for MESSAGE_STATUS_UPDATE events and updates campaign stats in real-time
  // Status string: 'sent' | 'delivered' | 'read' | 'failed'  (statusCode: 1/3/4/2)
  useEffect(() => {
    if (!statusUpdates || statusUpdates.length === 0) return;

    const latestUpdate = statusUpdates[statusUpdates.length - 1];
    if (!latestUpdate || !latestUpdate.campaignId) return;

    const status = latestUpdate.status;         // normalised string from websocketService
    const statusCode = latestUpdate.statusCode; // numeric code (may be undefined)

    // Skip queued messages (6) — they don't count towards progress
    if (statusCode === 6 || statusCode === '6' || status === 'queued') return;

    // Helper: recalculate all derived fields after count changes
    const recalcCampaign = (c) => {
      const sent  = c.sentCount      || 0;
      const deliv = c.deliveredCount  || 0;
      const seen  = c.seenCount       || 0;
      const fail  = c.failedCount     || 0;
      const total = c.totalRecipients || 0;

      const processedCount = sent + deliv + seen;
      const totalProcessed = processedCount + fail;
      const progressPercent = total > 0 ? Math.round((processedCount / total) * 100) : 0;

      const transentrytime = c.transentrytime;
      let isWithin48Hours = false;
      let isInProgress = false;
      if (transentrytime) {
        const hoursDiff = moment().diff(moment(transentrytime), 'hours');
        isWithin48Hours = hoursDiff < 48;
        isInProgress = (c.issent === 1 || c.issent === true)
                       && totalProcessed < total
                       && isWithin48Hours;
      }

      let statusVal = c.status;
      if (c.issent === 1 || c.issent === true) {
        if (total === 0 || totalProcessed >= total)  statusVal = 'completed';
        else if (isInProgress)                       statusVal = 'in_progress';
        else                                         statusVal = 'completed';
      }

      return {
        ...c,
        processedCount,
        totalProcessed,
        sent: processedCount,
        failed: fail,
        progressPercent,
        isInProgress,
        isWithin48Hours,
        status: statusVal
      };
    };

    const updateRow = (campaign) => {
      if (campaign.campaignid !== latestUpdate.campaignId) return campaign;

      const updated = { ...campaign };
      switch (status) {
        case 'sent':      updated.sentCount      = (updated.sentCount || 0) + 1; break;
        case 'failed':    updated.failedCount    = (updated.failedCount || 0) + 1; break;
        case 'delivered': updated.deliveredCount  = (updated.deliveredCount || 0) + 1; break;
        case 'read':      updated.seenCount      = (updated.seenCount || 0) + 1; break;
        default: break;
      }
      return recalcCampaign(updated);
    };

    setCampaigns(prev => prev.map(updateRow));
    setRecentBroadcasts(prev => prev.map(updateRow));
  }, [statusUpdates]);

  const fetchCampaigns = async (filters = activeFilters, page = currentPage, size = pageSize) => {
    try {
      setDataLoading(true);
      
      const requestBody = {
        campaignname: filters.campaignname || filters.search || "",
        templatename: filters.templatename || "",
        templateid: selectedTemplateId || "",
        search: filters.search || "",
        status: filters.status || "",
        page: page,
        pageSize: size
      };
      
      // Add date filters if present
      if (filters.dateFrom) {
        requestBody.dateFrom = moment(filters.dateFrom).isValid() 
          ? moment(filters.dateFrom).format('YYYY-MM-DD')
          : filters.dateFrom;
      }
      if (filters.dateTo) {
        requestBody.dateTo = moment(filters.dateTo).isValid() 
          ? moment(filters.dateTo).format('YYYY-MM-DD')
          : filters.dateTo;
      }
      
      // 117
      //  log to check request body
      // console.log('Campaign filter request body:', requestBody);
      
      const response = await axiosInstance.post('/campaign/details', requestBody);
      
      // Debug log to check response
      // console.log('Campaign filter response:', response.data);
      
      if (response.data.statuscode === 200 && response.data) {
        const campaignsData = response.data.entity.campaigns || [];
        
        // Debug log to check individual campaign data
        // console.log('Sample campaign data:', campaignsData[0]);
        // console.log('All campaign fields:', campaignsData[0] ? Object.keys(campaignsData[0]) : 'No campaigns');
        if (campaignsData[0]) {
          console.log('Campaign details:', {
            name: campaignsData[0].campaignname,
            successcount: campaignsData[0].successcount,
            failcount: campaignsData[0].failcount,
            issent: campaignsData[0].issent,
            status: campaignsData[0].status,
            bookid: campaignsData[0].bookid,
            templatemapping: campaignsData[0].templatemapping
          });
        }
        
        // Map API response to expected format
        const mappedCampaigns = campaignsData.map(campaign => {
          // Use new API fields for accurate counts
          const totalRecipients = parseInt(campaign.total_recipients || 0) || 0;
          const sentCount = parseInt(campaign.sent_count || campaign.sending_count || 0) || 0;
          const deliveredCount = parseInt(campaign.delivered_count || 0) || 0;
          const seenCount = parseInt(campaign.seen_count || 0) || 0;
          const failedCount = parseInt(campaign.failed_count || 0) || 0;

          // Total successfully processed messages (messages that reached any positive status)
          // In WhatsApp flow: Sent → Delivered → Seen (statuses are mutually exclusive)
          // So if seen_count=2, those 2 were sent & delivered before being seen
          const processedCount = sentCount + deliveredCount + seenCount;
          const totalProcessed = processedCount + failedCount;

          // Calculate progress percentage based on successfully processed messages only (excludes failed)
          const progressPercent = totalRecipients > 0 
            ? Math.round((processedCount / totalRecipients) * 100) 
            : 0;

          // Check if campaign is still in progress (within 48 hours of transentrytime)
          const transentrytime = campaign.transentrytime;
          let isWithin48Hours = false;
          let isInProgress = false;
          
          if (transentrytime) {
            const entryTime = moment(transentrytime);
            const now = moment();
            const hoursDiff = now.diff(entryTime, 'hours');
            isWithin48Hours = hoursDiff < 48;
            
            // Campaign is in progress if:
            // 1. It has been sent (issent=1)
            // 2. Not all messages processed yet (totalProcessed < totalRecipients)
            // 3. Still within 48 hour window
            isInProgress = campaign.issent === 1 && 
                          totalProcessed < totalRecipients && 
                          isWithin48Hours;
          }

          const scheduleStatus = normalizeScheduleStatus(campaign.schedule_status || campaign.scheduleStatus);

          // Determine status based on actual progress and schedule metadata
          let status = 'ready'; // Default to ready to send
          if (isScheduleLockedStatus(scheduleStatus)) {
            status = scheduleStatus;
          } else if (scheduleStatus === 'completed' || campaign.issent === 1) {
            if (totalRecipients === 0) {
              status = 'completed';
            } else if (totalProcessed >= totalRecipients) {
              // All messages processed (sent/delivered/seen/failed)
              status = 'completed';
            } else if (isInProgress) {
              status = 'in_progress';
            } else if (!isWithin48Hours && totalProcessed < totalRecipients) {
              // 48 hours passed but not all processed - mark as completed (timed out)
              status = 'completed';
            } else {
              status = 'completed';
            }
          }

          // Ratedetails from details API
          let ratedetails = campaign.ratedetails || null;
          try {
            if (typeof ratedetails === 'string') {
              ratedetails = JSON.parse(ratedetails);
            }
          } catch (e) {
            // Keep as-is if parsing fails
          }
          
          const actualTotalContacts = ratedetails?.totalContacts ?? totalRecipients ?? 0;
          const actualEstimatedCost = ratedetails?.finalPrice ?? null;
          const actualPricePerMessage = (ratedetails?.totalContacts && ratedetails?.finalPrice)
            ? Number(ratedetails.finalPrice) / Number(ratedetails.totalContacts)
            : (ratedetails?.contactsdetails && ratedetails.contactsdetails[0]?.pricepermessage)
              ? Number(ratedetails.contactsdetails[0].pricepermessage)
              : null;

          // Parse templatemapping
          let templatemapping = campaign.templatemapping || null;
          try {
            if (typeof templatemapping === 'string') {
              templatemapping = JSON.parse(templatemapping);
            }
          } catch (e) {
            // Keep as-is if parsing fails
          }

          return {
            id: campaign.campaignid,
            campaignid: campaign.campaignid,
            name: campaign.campaignname,
            template: campaign.templatename,
            templateCategory: campaign.templatecategory,
            // Preserve fields needed for cloning
            bookid: campaign.bookid,
            templatemapping: templatemapping,
            component: campaign.component,
            // Recipients data - use new API fields
            recipients: totalRecipients,
            totalRecipients: totalRecipients,
            // Sending progress data
            sendingCount: sentCount,
            sentCount: sentCount,
            deliveredCount: deliveredCount,
            seenCount: seenCount,
            failedCount: failedCount,
            processedCount: processedCount,
            totalProcessed: totalProcessed,
            // Legacy fields for backward compatibility
            sent: processedCount, // Use processed count for legacy 'sent' field
            failed: failedCount,
            // Progress calculation
            progressPercent: progressPercent,
            isInProgress: isInProgress,
            isWithin48Hours: isWithin48Hours,
            // Status
            status: status,
            // Timestamps
            date: campaign.entrytime,
            createdAt: campaign.entrytime,
            transentrytime: campaign.transentrytime,
            transupdatetime: campaign.transupdatetime,
            scheduled_at: campaign.scheduled_at || campaign.scheduledAt || null,
            schedule_status: scheduleStatus || null,
            scheduled_job_id: campaign.scheduled_job_id || campaign.scheduledJobId || null,
            send_requested_at: campaign.send_requested_at || campaign.sendRequestedAt || null,
            queued_at: campaign.queued_at || campaign.queuedAt || null,
            // Other fields
            companyid: campaign.companyid,
            templateid: campaign.templateid,
            flag: campaign.flag,
            issent: campaign.issent,
            cloneid: campaign.cloneid,
            mediaId: campaign.mediaid,
            originalData: campaign,
            // Opt-out info from /campaign/details
            optedOutCount: parseInt(campaign.opted_out_count || 0) || 0,
            // Ratedetails actual cost info
            ratedetails,
            actualTotalContacts,
            actualEstimatedCost,
            actualPricePerMessage,
            // BYO webhook fields — backend must include these in /campaign/details
            source: campaign.source || null,
            hard_send_cap: campaign.hard_send_cap ? parseInt(campaign.hard_send_cap) : null,
            audience_webhook_url: campaign.audience_webhook_url || null,
            audience_webhook_auth: campaign.audience_webhook_auth || null,
          };
        });

        setCampaigns(mappedCampaigns);
        setRecentBroadcasts(mappedCampaigns);
        setTotalCampaigns(response.data.entity.total || campaignsData.length);

        // Update stats based on mapped data
        setBroadcastStats({
          totalCampaigns: response.data.entity.total || campaignsData.length,
          activeCampaigns: mappedCampaigns.filter(c => c.status === 'in_progress' || c.status === 'active').length,
          sent: mappedCampaigns.reduce((sum, c) => sum + c.sent, 0),
          pending: mappedCampaigns.filter(c => c.status === 'pending' || c.status === 'draft').length,
          failed: mappedCampaigns.reduce((sum, c) => sum + c.failed, 0)
        });
      } else {
        // message.error(response.data?.message || 'Failed to load campaigns');
      }
      
    } catch (error) {
      // console.error('Error fetching campaigns:', error);
      // message.error('Failed to load campaigns');
      
      // Fallback to empty state
      setCampaigns([]);
      setRecentBroadcasts([]);
      setTotalCampaigns(0);
    } finally {
      setDataLoading(false);
    }
  };

  // Fetch plan status
  const fetchWalletBalance = async () => {
    try {
      setCreditsLoading(true);
      const planRes = await axiosInstance.get('/wallet/plan-status');
      const entity = planRes.data?.entity;
      if (entity && entity.has_active_plan !== undefined) {
        setPlanStatus(entity);
      }
    } catch (error) {
      console.error('Error fetching plan status:', error);
    } finally {
      setCreditsLoading(false);
    }
  };

  // Helper function to get category-specific avatar styling
  const getCategoryAvatar = (category) => {
    const categoryLower = (category || 'marketing').toLowerCase();
    
    const categoryStyles = {
      marketing: {
        icon: <BarChartOutlined />,
        backgroundColor: '#8b5cf6',
        lightBg: '#f5f3ff',
        accentColor: '#a78bfa',
        label: 'Marketing',
        initial: 'M'
      },
      utility: {
        icon: <ToolOutlined />,
        backgroundColor: '#ec4899',
        lightBg: '#fdf2f8',
        accentColor: '#f9a8d4',
        label: 'Utility',
        initial: 'U'
      },
      authentication: {
        icon: <LockOutlined />,
        backgroundColor: '#14b8a6',
        lightBg: '#f0fdfa',
        accentColor: '#5eead4',
        label: 'Authentication',
        initial: 'A'
      }
    };
    
    return categoryStyles[categoryLower] || categoryStyles.marketing;
  };

  // Table columns - premium SaaS design
  const columns = [
    {
      title: '#',
      key: 'srno',
      width: 52,
      align: 'center',
      render: (_, __, index) => (
        <span className="bp-srno">{(currentPage - 1) * pageSize + index + 1}</span>
      ),
    },
    {
      title: 'Campaign',
      dataIndex: 'name',
      key: 'name',
      render: (text, record) => {
        const cloneMatch = text.match(/^(.+?)\s*-\s*(Clone-\d+)$/);
        const categoryStyle = getCategoryAvatar(record.templateCategory);
        
        return (
          <div className="bp-campaign-cell">
            <div className="bp-campaign-avatar">
              <div
                className="bp-campaign-avatar-inner"
                style={{
                  background: categoryStyle.lightBg,
                  color: categoryStyle.backgroundColor,
                  border: `1.5px solid ${categoryStyle.backgroundColor}18`
                }}
              >
                <span className="bp-campaign-avatar-icon">{categoryStyle.icon}</span>
              </div>
              <div
                className="bp-campaign-avatar-badge"
                style={{ background: categoryStyle.backgroundColor }}
              >
                <span>{categoryStyle.initial}</span>
              </div>
            </div>
            <div className="bp-campaign-info">
              <div className="bp-campaign-name">
                {cloneMatch ? (
                  <>
                    {cloneMatch[1]}
                    <span className="bp-clone-badge">{cloneMatch[2]}</span>
                  </>
                ) : (
                  text
                )}
                {record.source === 'byo_webhook' && (
                  <Tag color="purple" style={{ marginLeft: 6, fontSize: 10, padding: '0 5px', lineHeight: '16px' }}>BYO Webhook</Tag>
                )}
              </div>
              <div className="bp-campaign-meta">
                <div
                  className="bp-category-dot"
                  style={{ background: categoryStyle.backgroundColor }}
                />
                <span
                  className="bp-category-label"
                  style={{ color: categoryStyle.backgroundColor }}
                >
                  {categoryStyle.label}
                </span>
                {record.scheduled_at && (
                  <span className="bp-sched-badge">
                    ⏰ Scheduled
                  </span>
                )}
              </div>
            </div>
          </div>
        );
      },
    },
    {
      title: 'Recipients',
      dataIndex: 'recipients',
      key: 'recipients',
      render: (_, record) => {
        const totalRecipients = record.totalRecipients || 0;
        const processedCount = record.processedCount || (record.sentCount + record.deliveredCount + record.seenCount) || 0;

        // BYO webhook: recipient count is unknown until send time
        if (record.source === 'byo_webhook') {
          const cap = record.hard_send_cap;
          return (
            <Tooltip title="Recipients are fetched live from your webhook at send time.">
              <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                <Tag color="purple" style={{ margin: 0, fontSize: 11 }}>BYO</Tag>
                <span style={{ fontSize: 12, color: '#6b7280' }}>
                  {cap ? `up to ${cap.toLocaleString()}` : 'webhook'}
                </span>
              </div>
            </Tooltip>
          );
        }
        
        if (totalRecipients === 0) {
          return (
            <Tooltip title="No active recipients. Users may have been deleted from the address book.">
              <div className="bp-recipients-warn">
                <TeamOutlined />
                <span className="bp-recipients-warn__count">0</span>
                <span style={{ fontSize: '11px' }}>⚠️</span>
              </div>
            </Tooltip>
          );
        }
        
        return (
          <div className="bp-recipients-cell">
            <TeamOutlined className="bp-recipients-icon" />
            <span className="bp-recipients-count">
              {processedCount}/{totalRecipients}
            </span>
            {record.failedCount > 0 && (
              <span className="bp-recipients-failed">
                ({record.failedCount} failed)
              </span>
            )}
          </div>
        );
      },
    },
    {
      title: 'Progress',
      key: 'progress',
      render: (_, record) => {
        const totalRecipients = record.totalRecipients || 0;
        const sentCount = record.sentCount || 0;
        const deliveredCount = record.deliveredCount || 0;
        const seenCount = record.seenCount || 0;
        const failedCount = record.failedCount || 0;
        const processedCount = record.processedCount || (sentCount + deliveredCount + seenCount) || 0;
        const progressPercent = record.progressPercent || 0;
        const isInProgress = record.isInProgress;
        const issent = record.issent;
        const isScheduled = record.scheduled_at && isScheduleLockedStatus(record.schedule_status);
        
        let progressStatus = 'normal';
        if (failedCount > 0 && processedCount === 0) {
          progressStatus = 'exception';
        } else if (isInProgress) {
          progressStatus = 'active';
        } else if (issent && progressPercent >= 100) {
          progressStatus = 'success';
        }
        
        const StatPill = ({ icon, count, color, label, pillClass }) => count > 0 ? (
          <Tooltip title={`${count} ${label}`}>
            <span className={`bp-stat-pill ${pillClass}`}>
              {icon} {count}
            </span>
          </Tooltip>
        ) : null;

        return (
          <div className="bp-progress-cell">
            <Progress 
              percent={issent ? progressPercent : 0} 
              size="small" 
              status={progressStatus}
              strokeColor={isInProgress ? {
                '0%': '#2563eb',
                '100%': '#10b981',
              } : progressPercent >= 100 ? '#10b981' : '#2563eb'}
              trailColor="#f1f5f9"
              format={() => issent ? `${progressPercent}%` : ''}
            />
            {issent ? (
              <div className="bp-stat-pills">
                <StatPill icon="✓" count={sentCount} color="#2563eb" label="Sent" pillClass="bp-stat-pill--sent" />
                <StatPill icon="✓✓" count={deliveredCount} color="#10b981" label="Delivered" pillClass="bp-stat-pill--delivered" />
                <StatPill icon="👁" count={seenCount} color="#7c3aed" label="Seen" pillClass="bp-stat-pill--seen" />
                <StatPill icon="✗" count={failedCount} color="#ef4444" label="Failed" pillClass="bp-stat-pill--failed" />
                {totalRecipients > 0 && (
                  <span className="bp-progress-total">/ {totalRecipients}</span>
                )}
                {isInProgress && (
                  <span className="bp-live-indicator">Live</span>
                )}
                {processedCount === 0 && failedCount === 0 && (
                  <span className="bp-progress-processing">
                    <LoadingOutlined style={{ fontSize: 10 }} /> Processing...
                  </span>
                )}
              </div>
            ) : (
              <div className="bp-progress-empty">
                {isScheduled ? `Scheduled for ${moment(record.scheduled_at).format('MMM D, h:mm A')}` : 'Not sent yet'}
              </div>
            )}
          </div>
        );
      },
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      render: (status, record) => {
        const config = {
          completed: { text: 'Completed', icon: <CheckCircleOutlined /> },
          in_progress: { text: 'In Progress', icon: <ThunderboltOutlined /> },
          failed: { text: 'Failed', icon: <CloseCircleOutlined /> },
          draft: { text: 'Draft', icon: <FileTextOutlined /> },
          ready: { text: 'Ready to Send', icon: <SendOutlined /> },
          scheduled: { text: 'Scheduled', icon: <CalendarOutlined /> },
          queued: { text: 'Queued', icon: <ClockCircleOutlined /> },
          processing: { text: 'Processing', icon: <LoadingOutlined /> },
          audience_fetching: { text: 'Audience Fetching', icon: <LoadingOutlined /> }
        };
        
        const displayStatus = record.status || (record.issent ? 'completed' : 'ready');
        const statusConfig = config[displayStatus] || { text: displayStatus?.toUpperCase(), icon: null };

        return (
          <div className="bp-status-cell">
            <Tag className={`bp-status-tag bp-status-tag--${displayStatus}`}>
              {statusConfig.icon} {statusConfig.text}
            </Tag>
            {record.optedOutCount > 0 && (
              <Tag className="bp-opted-out-tag">
                ⛔ {record.optedOutCount}/{record.recipients} opted out
              </Tag>
            )}

          </div>
        );
      },
    },
    {
      title: 'Date',
      dataIndex: 'date',
      key: 'date',
      render: (date, record) => (
        <div className="bp-date-cell bp-date-cell--stacked">
          <div className="bp-date-main">
            <CalendarOutlined />
            <span>{moment(date).format('MMM DD, YYYY')}</span>
          </div>
          {record.scheduled_at && (
            <div className="bp-date-sched">
              ⏰ {moment(record.scheduled_at).format('MMM D, h:mm A')}
            </div>
          )}
        </div>
      ),
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 140,
      render: (_, record) => (
        <div className="bp-actions-cell">
          <Tooltip title={record.issent === 0 ? 'Campaign must be sent before cloning' : 'Clone Campaign'}>
            <Button
              type="text"
              icon={<CopyOutlined />}
              onClick={() => handleCloneCampaign(record)}
              disabled={record.issent === 0}
              className="bp-action-btn bp-action-btn--clone"
            />
          </Tooltip>
          <Tooltip title={
            record.issent
              ? 'Campaign Already Sent'
              : isScheduleLockedStatus(record.schedule_status)
                ? `Campaign is already ${getScheduleStatusLabel(record.schedule_status)}`
              : record.source === 'byo_webhook'
                ? 'Send Campaign (recipients fetched from webhook at launch)'
                : (record.recipients > 0 && record.recipients - (record.optedOutCount || 0) <= 0)
                  ? 'All recipients opted out'
                  : 'Send Campaign'
          }>
            <Button
              type="text"
              icon={<SendOutlined />}
              onClick={() => {
                console.log('Send button clicked for campaign:', record);
                handleSendCampaign(record);
              }}
              disabled={
                !!record.issent ||
                isScheduleLockedStatus(record.schedule_status) ||
                (record.source !== 'byo_webhook' &&
                  record.recipients > 0 &&
                  record.recipients - (record.optedOutCount || 0) <= 0)
              }
              className="bp-action-btn bp-action-btn--send"
            />
          </Tooltip>
          <Tooltip title="View Details">
            <Button
              type="text"
              icon={<EyeOutlined />}
              onClick={() => {
                setSelectedCampaignId(record.campaignid);
                setShowCampaignDetails(true);
              }}
              className="bp-action-btn bp-action-btn--view"
            />
          </Tooltip>
        </div>
      )
    },
  ];

  // Handle clone campaign - uses optimized 'clone' selectionType
  // Backend fetches recipients from original campaign, avoiding large payload
  const handleCloneCampaign = async (campaign) => {
    try {
      setLoading(true);
      
      // Get the base campaign name (remove any existing clone suffix)
      const baseName = campaign.name.replace(/\s*-\s*Clone-\d+$/, '').trim();
      
      // Find existing clones to determine the next clone number
      const existingClones = campaigns.filter(c => 
        c.name.startsWith(baseName) && c.name.match(/\s*-\s*Clone-\d+$/)
      );
      
      // Extract clone numbers and find the next available number
      const cloneNumbers = existingClones.map(c => {
        const match = c.name.match(/Clone-(\d+)$/);
        return match ? parseInt(match[1]) : 0;
      }).filter(num => num > 0);
      
      const nextCloneNumber = cloneNumbers.length > 0 ? Math.max(...cloneNumbers) + 1 : 1;
      const cloneSuffix = `Clone-${nextCloneNumber.toString().padStart(2, '0')}`;

      // Use optimized 'clone' selectionType - backend fetches recipients from original campaign
      // This reduces payload from ~195KB (full user data) to ~1KB (just campaign ID)
      const clonePayload = {
        campaignname: `${baseName} - ${cloneSuffix}`,
        templateid: campaign.templateid,
        selectionType: 'clone',           // ✅ Use 'clone' type - backend handles recipient copying
        campaignid: campaign.campaignid,  // ✅ Original campaign ID to clone from
        campaigntype: '1',                // '1' = clone
        bookid: campaign.bookid || campaign.originalData?.bookid || null
      };

      // Normalize templatemapping shape to { variabletype, mapping }
      let templatemapping = campaign.templatemapping || campaign.originalData?.templatemapping || null;
      try {
        if (typeof templatemapping === 'string') {
          templatemapping = JSON.parse(templatemapping);
        }
      } catch (e) {
        // Leave as-is if parsing fails
      }
      clonePayload.templatemapping = templatemapping;

      // Include ratedetails if available from original campaign
      let ratedetails = campaign.ratedetails || campaign.originalData?.ratedetails || null;
      try {
        if (typeof ratedetails === 'string') {
          ratedetails = JSON.parse(ratedetails);
        }
      } catch (e) {
        // Leave as-is if parsing fails
      }
      if (ratedetails) {
        clonePayload.ratedetails = ratedetails;
      }

      console.log('Clone payload (optimized - no user data):', clonePayload);
      
      const response = await axiosInstance.post('/campaign/create', clonePayload);
      
      // Handle 400 status for no active users
      if (response.data && response.data.statuscode === 400) {
        // Show user-friendly error message for no active users scenarios
        Modal.warning({
          title: 'Cannot Clone Campaign',
          content: response.data.message || 'No active users found from the original campaign. All users may have been deleted from the address book.',
          okText: 'OK'
        });
        return;
      }
      
      if (response.data && response.data.statuscode === 200) {
        message.success('Campaign cloned successfully');
        // Refresh the campaigns list
        fetchCampaigns();
      } else {
        message.error(response.data?.message || 'Failed to clone campaign');
      }
    } catch (error) {
      console.error('Error cloning campaign:', error);
      // Handle axios error response for 400 status
      if (error.response && error.response.data && error.response.data.statuscode === 400) {
        Modal.warning({
          title: 'Cannot Clone Campaign',
          content: error.response.data.message || 'No active users found from the original campaign.',
          okText: 'OK'
        });
      } else {
        message.error('Failed to clone campaign. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  // eslint-disable-next-line no-unused-vars
  const steps = [
    {
      title: 'Select Recipients',
      description: 'Choose contacts to send messages to',
      icon: <TeamOutlined />,
    },
    {
      title: 'Choose Template',
      description: 'Select a message template',
      icon: <FileTextOutlined />,
    },
    {
      title: 'Review & Send',
      description: 'Review your broadcast and send',
      icon: <SendOutlined />,
    },
  ];

  // Handler functions for campaign actions
  // eslint-disable-next-line no-unused-vars
  const handleDeleteCampaign = (campaignId) => {
    Modal.confirm({
      title: 'Delete Campaign',
      content: 'Are you sure you want to delete this campaign? This action cannot be undone.',
      icon: <DeleteOutlined />,
      okText: 'Delete',
      okType: 'danger',
      onOk: async () => {
        try {
          setLoading(true);
          // TODO: Add API call to delete campaign
          // await api.deleteCampaign(campaignId);
          
          // Update local state
          setRecentBroadcasts(prev => prev.filter(campaign => campaign.campaignid !== campaignId));
          setCampaigns(prev => prev.filter(campaign => campaign.campaignid !== campaignId));
          setTotalCampaigns(prev => prev - 1);
          
          await fetchCampaigns(); // Refresh data
        } catch (error) {
          message.error('Failed to delete campaign');
        } finally {
          setLoading(false);
        }
      }
    });
  };

  // Pre-send validation to warn users about low recipient counts
  const validateBeforeSend = (campaign) => {
    // BYO webhook campaigns: recipients are fetched from the webhook at launch time.
    // There is no pre-known count, so all address-book checks are skipped.
    if (campaign.source === 'byo_webhook') {
      return true;
    }

    const totalRecipients = parseInt(campaign.recipients) || parseInt(campaign.totalRecipients) || parseInt(campaign.total_recipients) || 0;

    // If no recipients, show error immediately
    if (totalRecipients === 0) {
      Modal.warning({
        title: 'No Active Recipients',
        content: 'This campaign has no active recipients. Users may have been deleted from the address book.',
        okText: 'OK'
      });
      return false;
    }

    // If all recipients opted out, block sending
    const optedOutCount = parseInt(campaign.optedOutCount || campaign.opted_out_count) || 0;
    if (optedOutCount > 0 && totalRecipients - optedOutCount <= 0) {
      Modal.warning({
        title: 'All Recipients Opted Out',
        content: `All ${totalRecipients} recipient(s) have opted out of ${(campaign.templateCategory || 'marketing').toLowerCase()} campaigns. This campaign cannot be sent.`,
        okText: 'OK'
      });
      return false;
    }

    // If low recipient count, show warning but allow proceeding
    if (totalRecipients > 0 && totalRecipients < 5) {
      Modal.confirm({
        title: 'Low Recipient Count',
        content: `This campaign only has ${totalRecipients} recipient(s). Some users may have been deleted. Do you want to proceed?`,
        okText: 'Send Anyway',
        cancelText: 'Cancel',
        onOk: () => {
          setSelectedCampaign(campaign);
          setSendCampaignVisible(true);
        }
      });
      return false; // Return false since we're handling it in onOk callback
    }

    return true;
  };

  const handleSendCampaign = (campaign) => {
    // Validate before opening send modal
    if (!validateBeforeSend(campaign)) {
      return;
    }
    setSelectedCampaign(campaign);
    setSendCampaignVisible(true);
  };

  const handleSendConfirm = async (sendData) => {
    try {
      setLoading(true);
      
      // Prepare the new payload structure
      const payload = {
        campaignid: sendData.campaignId,
        totalcontacts: sendData.totalcontacts,
        pricepermessage: sendData.pricepermessage,
        estimatecost: sendData.estimatecost,
        messagecategory: sendData.messagecategory,
        mediaId: selectedCampaign?.mediaId, // Include mediaId from selected campaign
        sendMode: sendData.sendMode || 'now',
        ...(sendData.sendMode === 'scheduled' && sendData.scheduledAt
          ? { scheduledAt: sendData.scheduledAt }
          : {})
      };
      
      console.log('Sending campaign with payload:', payload);
      
      const response = await axiosInstance.post('/campaign/send', payload);
      
      // Handle 400 status for no active users
      if (response.data && response.data.statuscode === 400) {
        setSendCampaignVisible(false);
        Modal.warning({
          title: 'Cannot Send Campaign',
          content: response.data.message || 'No active users found in the address book. All users may have been deleted.',
          okText: 'OK'
        });
        return;
      }
      
      if (response.data && response.data.status && [200, 202].includes(response.data.statuscode)) {
        setSendCampaignVisible(false);

        const respEntity = response.data.entity || response.data;
        const isByoCampaign = selectedCampaign?.source === 'byo_webhook';
        const isScheduledSend = sendData.sendMode === 'scheduled' || response.data.statuscode === 202 || respEntity.status === 'scheduled';
        const scheduledAt = respEntity.scheduledAt || sendData.scheduledAt || null;

        if (isScheduledSend) {
          message.success(response.data.message || 'Campaign scheduled for sending');
        } else if (isByoCampaign) {
          // Show a descriptive success modal for BYO — a small toast is too easy to miss
          Modal.success({
            title: 'Campaign Launched',
            width: 480,
            content: (
              <div style={{ lineHeight: 1.8 }}>
                <p>
                  <strong>{selectedCampaign?.name}</strong> is now queued and will start
                  streaming recipients from your webhook immediately.
                </p>
                <ul style={{ paddingLeft: 20, marginBottom: 8 }}>
                  <li>Recipients are fetched page-by-page from your webhook endpoint</li>
                  <li>
                    Sending stops when the webhook returns <code>null</code> or the hard cap of{' '}
                    <strong>{(selectedCampaign?.hard_send_cap || 0).toLocaleString()}</strong> is reached
                  </li>
                  <li>Delivery stats will appear in real-time as messages are processed</li>
                </ul>
                <p style={{ color: '#8c8c8c', fontSize: 12, marginBottom: 0 }}>
                  {response.data.message || 'Campaign queued for processing'}
                </p>
              </div>
            ),
            okText: 'Got It',
          });
        } else {
          // Address-book campaign — brief toast is enough
          const optedOutUsers = respEntity.optedOutUsers || 0;
          const totalPendingUsers = respEntity.totalPendingUsers || 0;
          if (optedOutUsers > 0) {
            message.success(`Campaign sent to ${totalPendingUsers} recipients (${optedOutUsers} opted-out skipped)`);
          } else {
            message.success(response.data.message || 'Campaign queued for processing');
          }
        }
        
        // Immediately update the local campaign state to reflect the send/schedule status
        const campaignId = sendData.campaignId;
        const currentTime = new Date().toISOString();
        const localUpdate = isScheduledSend
          ? {
              issent: 0,
              status: 'scheduled',
              scheduled_at: scheduledAt,
              schedule_status: 'scheduled',
              scheduled_job_id: respEntity.jobId || null,
              send_requested_at: currentTime,
              isInProgress: false,
              isWithin48Hours: false
            }
          : {
              issent: 1,
              status: 'in_progress',
              transentrytime: currentTime,
              transupdatetime: currentTime,
              isInProgress: true,
              isWithin48Hours: true
            };
        
        setCampaigns(prevCampaigns => 
          prevCampaigns.map(campaign => 
            campaign.campaignid === campaignId 
              ? { ...campaign, ...localUpdate }
              : campaign
          )
        );
        
        // Also update recentBroadcasts state
        setRecentBroadcasts(prevBroadcasts => 
          prevBroadcasts.map(campaign => 
            campaign.campaignid === campaignId 
              ? { ...campaign, ...localUpdate }
              : campaign
          )
        );
        
        // Refresh campaigns data from API to get the latest server state
        await fetchCampaigns();
        
        // Refresh plan status and wallet balance since a campaign was sent
        fetchWalletBalance();
      } else {
        message.error(response.data?.message || 'Failed to send campaign');
      }
    } catch (error) {
      // Handle axios error response for 400 status
      if (error.response && error.response.data && error.response.data.statuscode === 400) {
        setSendCampaignVisible(false);
        // Check if it's a quota exceeded error
        const errMsg = error.response.data.message || '';
        const isQuotaError = errMsg.toLowerCase().includes('quota') || errMsg.toLowerCase().includes('limit') || errMsg.toLowerCase().includes('plan');
        Modal.warning({
          title: isQuotaError ? 'Campaign Quota Exceeded' : 'Cannot Send Campaign',
          content: isQuotaError 
            ? (errMsg + ' Please upgrade your plan or wait for the next billing cycle.')
            : (errMsg || 'No active users found in the address book.'),
          okText: isQuotaError ? 'View Plans' : 'OK',
          onOk: isQuotaError ? () => window.location.href = '/dashboard/pricing' : undefined,
        });
      } else if (error.response && error.response.status === 403) {
        // Plan/quota restriction from backend
        setSendCampaignVisible(false);
        Modal.error({
          title: 'Plan Limit Reached',
          content: error.response.data?.message || 'You have reached your campaign limit. Please upgrade your plan.',
          okText: 'Upgrade Plan',
          onOk: () => window.location.href = '/dashboard/pricing',
        });
      } else {
        message.error('Failed to send campaign. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleCreateCampaign = (_campaignData) => {
    setLoading(true);
    setTimeout(() => {
      setLoading(false);
      setShowAdvancedBuilder(false);
      // message.success('Campaign created successfully');
      fetchCampaigns(); // Refresh campaigns list
    }, 1000);
  };

  // eslint-disable-next-line no-unused-vars
  const stats = {
    totalCampaigns: broadcastStats.totalCampaigns || 0,
    activeCampaigns: broadcastStats.activeCampaigns || 0,
    sent: broadcastStats.sent || 0,
    pending: broadcastStats.pending || 0,
    failed: broadcastStats.failed || 0,
  };

  // Show Advanced Campaign Builder
  if (showAdvancedBuilder) {
    return (
      <AdvancedBroadcastBuilder
        onBack={() => setShowAdvancedBuilder(false)}
        onSave={handleCreateCampaign}
      />
    );
  }

  // Show Campaign Details Page
  if (showCampaignDetails) {
    const selectedCampaignData = campaigns.find(c => c.campaignid === selectedCampaignId);
    
    return (
      <CampaignDetailsPage
        campaignId={selectedCampaignId}
        campaignData={selectedCampaignData}
        sendLoading={loading}
        onBack={() => {
          setShowCampaignDetails(false);
          setSelectedCampaignId(null);
        }}
        onSendCampaign={handleSendCampaign}
        onCampaignUpdated={() => {
          // Refresh campaigns list when recipients are added/removed
          fetchCampaigns(activeFilters, currentPage, pageSize);
        }}
      />
    );
  }

  // Helper: check if advanced filters are active (exclude search/campaignname)
  const hasActiveAdvancedFilters = Object.keys(activeFilters).some(key => 
    activeFilters[key] && key !== 'search' && key !== 'campaignname'
  );
  const activeAdvancedFilterCount = Object.keys(activeFilters).filter(key => 
    activeFilters[key] && key !== 'search' && key !== 'campaignname'
  ).length;

  return (
    <div className="bp-page-wrapper">
      {/* WABA Setup Notice */}
      {!loadingWabaDetails && !hasWabaDetails && !wabaFetchError && (
        <div className="bp-waba-notice">
          <span className="bp-waba-notice__text">
            <InfoCircleOutlined />
            Complete WhatsApp Business setup to create campaigns
          </span>
          <a 
            onClick={() => navigate('/whatsappregistration')}
            className="bp-waba-notice__link"
          >
            Set up now →
          </a>
        </div>
      )}

      {/* Page Header */}
      <div className="bp-header">
        <div className="bp-header__left">
          <h2 className="bp-page-title">Campaigns</h2>
          <p className="bp-page-subtitle">Manage and monitor your broadcast campaigns</p>
        </div>

        {/* Plan / Credits Badge */}
        <div className="bp-plan-badge">
          <div className="bp-plan-badge__icon">
            <ThunderboltOutlined />
          </div>
          <div className="bp-plan-badge__content">
            {creditsLoading ? (
              <div className="bp-skeleton-shimmer" style={{ height: 16, width: 100 }} />
            ) : planStatus?.has_active_plan ? (
              <>
                <span className="bp-plan-badge__name">
                  {planStatus.display_name || planStatus.plan_name}
                </span>
                <span className="bp-plan-badge__detail">
                  {planStatus.whatsapp?.is_unlimited 
                    ? '∞ Unlimited Campaigns' 
                    : `${planStatus.whatsapp?.campaigns_remaining ?? 0} Campaigns Left`}
                </span>
              </>
            ) : (
              <span className="bp-plan-badge__no-plan">No Active Plan</span>
            )}
          </div>
          {!creditsLoading && planStatus?.has_active_plan && planStatus?.days_remaining != null && planStatus.days_remaining <= 7 && (
            <Button 
              type="primary" 
              size="small"
              onClick={() => navigate('/dashboard/pricing')} 
              className="bp-plan-renew-btn"
            >
              Renew ({planStatus.days_remaining}d left)
            </Button>
          )}
        </div>
      </div>

      {/* Toolbar: Search, Filter, Actions */}
      <div className="bp-toolbar">
        <div className="bp-toolbar__left">
          <Input
            placeholder="Search campaigns..."
            prefix={searchLoading ? <LoadingOutlined /> : <SearchOutlined />}
            className="bp-search-input"
            allowClear
            onChange={(e) => handleSearch(e.target.value)}
            value={searchText}
          />
          
          <Button
            icon={<FilterOutlined />}
            onClick={() => setAdvancedFilterVisible(true)}
            className={`bp-filter-btn ${hasActiveAdvancedFilters ? 'bp-filter-btn--active' : ''}`}
          >
            Filters
            {hasActiveAdvancedFilters && (
              <span className="bp-filter-badge">{activeAdvancedFilterCount}</span>
            )}
          </Button>
        </div>

        <div className="bp-toolbar__right">
          <Button 
            icon={<ReloadOutlined />} 
            onClick={handleRefresh}
            loading={dataLoading}
            disabled={dataLoading || refreshCooldown}
            title={refreshCooldown ? 'Please wait before refreshing again' : 'Refresh data'}
            className="bp-refresh-btn"
          >
            Refresh
          </Button>
          
          <WriteGuard page="campaigns">
            <Button 
              type="primary" 
              icon={<PlusOutlined />}
              onClick={() => setShowAdvancedBuilder(true)}
              disabled={!loadingWabaDetails && !hasWabaDetails && !wabaFetchError}
              className="bp-create-btn"
            >
              Create Campaign
            </Button>
          </WriteGuard>
        </div>
      </div>

      {/* Campaigns Table */}
      <BoneSkeleton name="broadcast-table" loading={dataLoading && campaigns.length === 0} fallback={<CampaignTableSkeleton />}>
        <Card className="bp-table-card" styles={{ body: { padding: 0 } }}>
          <Table
            columns={columns}
            dataSource={campaigns}
            rowKey={(record) => record.id || record.campaignid || record._id || Math.random()}
            pagination={{
              current: currentPage,
              pageSize: pageSize,
              total: totalCampaigns,
              showSizeChanger: true,
              showQuickJumper: true,
              showTotal: (total, range) =>
                `${range[0]}–${range[1]} of ${total} campaigns`,
              onChange: (page, size) => {
                setCurrentPage(page);
                if (size !== pageSize) {
                  setPageSize(size);
                }
              },
            }}
            scroll={{ x: 800 }}
          />
        </Card>
      </BoneSkeleton>

      {/* Campaign Detail Modal */}
      <Modal
        title="Campaign Details"
        visible={campaignDetailVisible}
        onCancel={() => setCampaignDetailVisible(false)}
        footer={null}
        width={700}
        className="bp-detail-modal"
      >
        {selectedCampaign && (
          <div>
            <div style={{ marginBottom: 24 }}>
              <Title level={4}>{selectedCampaign.name}</Title>
              <Text type="secondary">Template: {selectedCampaign.template}</Text>
            </div>

            <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
              <Col span={8}>
                <Card size="small">
                  <Statistic
                    title="Recipients"
                    value={selectedCampaign.recipients}
                    prefix={<TeamOutlined />}
                  />
                </Card>
              </Col>
              <Col span={8}>
                <Card size="small">
                  <Statistic
                    title="Sent"
                    value={selectedCampaign.sent}
                    prefix={<CheckCircleOutlined style={{ color: '#10b981' }} />}
                    valueStyle={{ color: '#10b981' }}
                  />
                </Card>
              </Col>
              <Col span={8}>
                <Card size="small">
                  <Statistic
                    title="Failed"
                    value={selectedCampaign.failed}
                    prefix={<CloseCircleOutlined style={{ color: '#ef4444' }} />}
                    valueStyle={{ color: '#ef4444' }}
                  />
                </Card>
              </Col>
            </Row>
          </div>
        )}
      </Modal>

      {/* Send Campaign Modal */}
      <SendCampaignModal
        visible={sendCampaignVisible}
        onCancel={() => {
          setSendCampaignVisible(false);
          setSelectedCampaign(null);
        }}
        onSend={handleSendConfirm}
        campaign={selectedCampaign}
        loading={loading}
        campaignId={selectedCampaign?.id || selectedCampaign?.campaignid || ''}
      />

      {/* Campaign Send Result Modal removed, now using toast for response message */}

      {/* Advanced Filter Modal */}
      <Modal
        title="Advanced Filters"
        open={advancedFilterVisible}
        onOk={() => {
          filterForm.validateFields().then(values => {
            // Convert date objects to proper format and merge with existing filters
            const processedValues = { ...values };
            if (processedValues.dateFrom) {
              processedValues.dateFrom = moment(processedValues.dateFrom).isValid() 
                ? moment(processedValues.dateFrom).format('YYYY-MM-DD')
                : processedValues.dateFrom;
            }
            if (processedValues.dateTo) {
              processedValues.dateTo = moment(processedValues.dateTo).isValid() 
                ? moment(processedValues.dateTo).format('YYYY-MM-DD')
                : processedValues.dateTo;
            }
            
            const newFilters = { 
              ...activeFilters, 
              ...processedValues,
              search: searchText,
              campaignname: searchText 
            };
            setActiveFilters(newFilters);
            fetchCampaigns(newFilters, 1, pageSize);
            setCurrentPage(1);
            setAdvancedFilterVisible(false);
          });
        }}
        onCancel={() => {
          setAdvancedFilterVisible(false);
          filterForm.resetFields();
        }}
        okText="Apply Filters"
        cancelText="Cancel"
        width={560}
        className="bp-filter-modal"
      >
        <Form
          form={filterForm}
          layout="vertical"
          initialValues={{
            ...activeFilters,
            dateFrom: activeFilters.dateFrom ? moment(activeFilters.dateFrom) : null,
            dateTo: activeFilters.dateTo ? moment(activeFilters.dateTo) : null
          }}
        >
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="templatename"
                label="Template Name"
              >
                <Input placeholder="Filter by template name" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="status"
                label="Campaign Status"
              >
                <Select placeholder="Select status" allowClear>
                  <Option value="active">Active</Option>
                  <Option value="inactive">Inactive</Option>
                  <Option value="completed">Completed</Option>
                  <Option value="draft">Draft</Option>
                </Select>
              </Form.Item>
            </Col>
          </Row>
          
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="dateFrom"
                label="Created From"
                rules={[
                  ({ getFieldValue }) => ({
                    validator(_, value) {
                      const dateTo = getFieldValue('dateTo');
                      if (value && dateTo && moment(value).isAfter(moment(dateTo))) {
                        return Promise.reject(new Error('Start date must be before end date'));
                      }
                      return Promise.resolve();
                    },
                  }),
                ]}
              >
                <DatePicker 
                  style={{ width: '100%' }} 
                  placeholder="Select start date"
                  format="YYYY-MM-DD"
                  allowClear
                />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="dateTo"
                label="Created To"
                rules={[
                  ({ getFieldValue }) => ({
                    validator(_, value) {
                      const dateFrom = getFieldValue('dateFrom');
                      if (value && dateFrom && moment(value).isBefore(moment(dateFrom))) {
                        return Promise.reject(new Error('End date must be after start date'));
                      }
                      return Promise.resolve();
                    },
                  }),
                ]}
              >
                <DatePicker 
                  style={{ width: '100%' }} 
                  placeholder="Select end date"
                  format="YYYY-MM-DD"
                  allowClear
                />
              </Form.Item>
            </Col>
          </Row>

          <Row>
            <Col span={24}>
              <div style={{ textAlign: 'right', marginTop: 8 }}>
                <Button 
                  className="bp-filter-clear-btn"
                  onClick={() => {
                    filterForm.resetFields();
                    const clearedFilters = { 
                      search: searchText, 
                      campaignname: searchText,
                      templatename: "",
                      status: "",
                      dateFrom: null,
                      dateTo: null
                    };
                    setActiveFilters(clearedFilters);
                    fetchCampaigns(clearedFilters, 1, pageSize);
                    setCurrentPage(1);
                  }}
                >
                  Clear All
                </Button>
              </div>
            </Col>
          </Row>
        </Form>
      </Modal>
    </div>
  );
};

export default memo(BroadcastPage);